import { Grid, Typography } from '@mui/material';
import Button from 'components/Button';
import MultiPageBuilder from 'components/MultiPageBuilder';
import PageLoader from 'components/PageLoader';
import PendingDraftEqualisationsModal from 'components/PendingDraftEqualisationsModal';
import FullHeightView from 'components/new/FullHeightView';
import { useLayoutContext } from 'contexts/LayoutContext';
import { Column, Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import { numberToCurrencyString } from 'further-ui/utils';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  UpdateCapitalActionFormValues,
  Action,
} from '../hooks/useCapitalActionForm';
import useGetEntryTitle from '../hooks/useGetEntryTitle';
import InvestorBreakdown from './InvestorBreakdown';
import TransactionsReview from './TransactionsReview';
import useReviewDetails from './hooks/useReviewDetails';

type Props = {
  lastAction: Action | null;
  onPrevious: () => void;
  onSaveAndExit: () => void;
  onSubmit: () => void;
  isLoading: boolean;
};

const CapitalActionReview: React.FC<Props> = ({
  lastAction,
  onPrevious,
  onSaveAndExit,
  onSubmit,
  isLoading,
}) => {
  const { id } = useParams<{ id?: string }>();
  const getEntryTitle = useGetEntryTitle();
  const { vehicle } = useLayoutContext();
  const { hasEqualisationInDraftState } = useEqualisations(vehicle?._id);
  const confirmDialog = useDisclosure();

  const { getValues } = useFormContext<UpdateCapitalActionFormValues>();

  const entries = getValues('entries') ?? [];

  const {
    investorsBreakdown,
    totalAmount,
    isLoading: isReviewDetailsLoading,
  } = useReviewDetails({
    capitalActionId: id,
    entries,
    vehicleId: getValues('vehicleId'),
    date: getValues('ledgerDate'),
  });

  if (isReviewDetailsLoading) {
    return <PageLoader />;
  }

  return (
    <FullHeightView
      actionBar={
        <Row centered spacing="sm" justify="space-between">
          <Button
            size="large"
            variant="outlined"
            onClick={onPrevious}
            disabled={isLoading}
          >
            Previous step
          </Button>
          <Row centered spacing="sm" justify="flex-end">
            <Button
              size="large"
              variant="outlined"
              onClick={onSaveAndExit}
              loading={lastAction === 'saveAndExit' && isLoading}
              disabled={isLoading}
            >
              Save and exit
            </Button>
            <Button
              size="large"
              variant="contained"
              onClick={
                hasEqualisationInDraftState ? confirmDialog.onOpen : onSubmit
              }
              loading={lastAction === 'saveAndNext' && isLoading}
              disabled={isLoading}
            >
              Save and approve
            </Button>
          </Row>
        </Row>
      }
    >
      <Grid container>
        <Grid size={{ xs: 12, lg: 11 }}>
          <Column spacing="md">
            <Typography variant="h4">
              Action overall investor breakdown
            </Typography>
            <InvestorBreakdown
              investorsBreakdown={investorsBreakdown}
              baseCurrency={vehicle?.baseCurrency}
            />
            <MultiPageBuilder
              summaryHeader="Capital action summary"
              summaryFooterTitle="Total to be called/(distributed)"
              summaryFooterTotals={
                <Typography fontWeight="bold">
                  {numberToCurrencyString(totalAmount, {
                    currency: vehicle?.baseCurrency,
                    negativeInBrackets: true,
                  })}
                </Typography>
              }
              defaultActivePageIndex={0}
              showSummaryOnly
            >
              {entries.map((entry, index) => (
                <MultiPageBuilder.Page
                  key={`entry-${index}`}
                  title={getEntryTitle(entry)}
                  totals={numberToCurrencyString(entry.totalAmountLcy, {
                    currency: vehicle?.baseCurrency,
                  })}
                />
              ))}
            </MultiPageBuilder>
            <Typography variant="h4">Review accounts transactions</Typography>
            <TransactionsReview vehicleId={vehicle?._id ?? ''} />
          </Column>
        </Grid>
        {hasEqualisationInDraftState && (
          <PendingDraftEqualisationsModal
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            onConfirm={onSubmit}
            loading={isLoading}
          />
        )}
      </Grid>
    </FullHeightView>
  );
};

export default CapitalActionReview;
